import { LitElement, html, css } from 'lit';
import { Router } from '@vaadin/router';
import { insertNewTickerDataIfMissing } from '../../shared/utils/util.js';
import searchIcon from '../../images/search-icon-dark.svg';
import './ticker-search-results-element.js';

class TickerSearchSidebarElement extends LitElement {
  static get properties() {
    return {
      searchText: String,
      isCardVisible: Boolean,
    };
  }

  constructor() {
    super();
    this.searchText = '';
    this.isCardVisible = false;
    this._debounceTimer = null;
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  handleOutsideClick(event) {
    const card = this.shadowRoot.querySelector('ticker-search-results-element');
    const input = this.shadowRoot.querySelector('.filter-search');
    const path = event.composedPath();

    if (this.isCardVisible && card && input) {
      const isClickInsideCard = path.includes(card);
      const isClickInsideInput = path.includes(input);
      
      if (!isClickInsideCard && !isClickInsideInput) {
        this.isCardVisible = false;
        this.requestUpdate();
      }
    }
  }

  handleSearchInput(event) {
    this.searchText = event.target.value;
    
    clearTimeout(this._debounceTimer);
    
    if (this.searchText && this.searchText.trim() !== '') {
      // Show card immediately when user starts typing
      this.isCardVisible = true;
      
      // Fetch results after debounce
      this._debounceTimer = setTimeout(() => {
        this.isCardVisible = true;
        this.shadowRoot.querySelector('ticker-search-results-element').fetchSearchResults();
      }, 300);
    } else {
      // Hide card when input is empty
      this.isCardVisible = false;
    }
  }

  handleInputFocus() {
    if (this.searchText && this.searchText.trim() !== '') {
      this.isCardVisible = true;
    }
  }

  async handleTickerChanged(event) {
    const symbol = event.detail.symbol;
    this.searchText = '';
    this.isCardVisible = false;
    
    // Dispatch event to close the navigation drawer
    this.dispatchEvent(new CustomEvent('close-drawer', { 
      bubbles: true, 
      composed: true 
    }));
    
    // Insert data for this ticker if we've never seen it before
    await insertNewTickerDataIfMissing(symbol);
    
    Router.go(`/app/analytics/${symbol}`);
  }

  static get styles() {
    return css`
      .sidebar-search-bar-container {
        display: flex;
        justify-content: center;
        width: calc(100% - 48px);
        padding: 0 24px;

        @media (max-width: 1479px) {
          padding: 0 15px;
          width: calc(100% - 30px);
        }

        @media (max-width: 1199px) {
          padding: 0 10px;
          width: calc(100% - 20px);
        }

        @media (max-width: 1023px) {
          padding: 0 20px;
          width: calc(100% - 40px);
        }
      }
      .sidebar-search-bar {
        display: flex;
        align-items: center;
        background: #FFF;
        border-radius: 8px;
        width: 100%;
        position: relative;
        height: 40px;
      }
      .sidebar-search-bar-relative {
        position: relative;
        width: 100%;
      }
      .sidebar-search-icon {
        position: absolute;
        left: 10px;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 40px;
      }
      .sidebar-search-icon img {
        width: 16px;
        height: 16px;
      }
      .filter-search {
        width: 100%;
        padding: 10px 10px 10px 40px;
        border: 1px solid #EFF1F7;
        background: #FAFAFC;
        font-size: 15px;
        outline: none;
        height: 40px;
        line-height: 20px;
        box-sizing: border-box;
      }
      .filter-search::placeholder {
        color: #535362;
        opacity: 1;
      }
    `;
  }

  render() {
    return html`
      <div class="sidebar-search-bar-container">
        <div class="sidebar-search-bar-relative">
          <div class="sidebar-search-bar">
            <span class="sidebar-search-icon">
              <img src="${searchIcon}" alt="Search" />
            </span>
            <input
              class="filter-search"
              type="text"
              placeholder="Search for a ticker"
              .value=${this.searchText}
              @input=${this.handleSearchInput}
              @focus=${this.handleInputFocus}
            />
          </div>
          <ticker-search-results-element
            .searchText=${this.searchText}
            .isCardVisible=${this.isCardVisible}
            .showExchangeButtons=${false}
            @ticker-changed=${this.handleTickerChanged}
          ></ticker-search-results-element>
        </div>
      </div>
    `;
  }
}

customElements.define('ticker-search-sidebar-element', TickerSearchSidebarElement); 